/* eslint-disable no-console */
import { LOCATION_CHANGE } from 'connected-react-router';
import { combineEpics, Epic, ofType } from 'redux-observable';
import { EMPTY, timer } from 'rxjs';
import {
  catchError,
  filter,
  map,
  mergeMap,
  switchMap,
  takeUntil,
  takeWhile,
} from 'rxjs/operators';

import {
  dispatchHoldingApi,
  dispatchHoldingsApiStart,
  setHoldingApisTimeStamp,
} from 'actions/other';
import { deactivateRestMode, manualRestMode } from 'actions/socket';
import {
  getOpenOrdersFromApi,
  getOpenStopOrdersFromApi,
  getPositionsFromApi,
} from 'actions/trade';
import { isAuthenticated } from 'actions/user';
import { getBalances } from 'actions/wallet';
import { TAB_CHANGE } from 'actionTypes/appDrawer';
import BASKET_ORDERS from 'actionTypes/basketorders';
import { DISPATCH_HOLDINGS_API } from 'actionTypes/other';
import {
  AUTH_CHANNEL_RETRY,
  AUTHENTICATE_SOCKET,
  DEACTIVATE_POLLING,
  DISCONNECT_SOCKET,
  MANUAL_POLLING_ACTIVATE,
} from 'actionTypes/socket';
import TRADE from 'actionTypes/trade';
import { SET_AUTH_SOCKET_CHANNEL_STATUS } from 'actionTypes/user';
import { DESKTOP_WALLET_OPEN } from 'actionTypes/wallet';
import { postMessageToMobileApp } from 'components/app/helpers';
import { AUTH_CHANNEL_STATUS } from 'constants/authChannel';
import { isRNWebview } from 'constants/constants';
import { POST_MESSAGE_TO_MOBILE_EVENTS } from 'constants/enums';
import { timeNow } from 'helpers/day';
import { getHoldingsApTimestampSelector } from 'selectors/otherSelectors';
import { isOnlineSelector } from 'selectors/tradeSelectors';
import { selectIsAuthChannelFailed } from 'selectors/user';
import IState from 'types/Istore';

import { hasAnyOpenPollingAsset, shouldStartPolling } from './utils/restMode';

const REST_MODE_DELAY = 60_000;
const RETRY_AUTHCHANNEL_DELAY = 50_000;

export const rmHoldingsApi: Epic<any, any, IState> = (action$, state$) =>
  action$.pipe(
    ofType(MANUAL_POLLING_ACTIVATE),
    filter(() => {
      return (
        isAuthenticated(state$.value.user) &&
        selectIsAuthChannelFailed(state$.value) &&
        isOnlineSelector(state$.value)
      );
    }),
    switchMap(({ payload }) => {
      console.log(`polling ${payload.retrigger ? 'restarted' : 'started'}`);

      const initialApiCallDelay = payload.retrigger ? REST_MODE_DELAY : 0;
      return timer(initialApiCallDelay, REST_MODE_DELAY).pipe(
        takeUntil(action$.ofType(DEACTIVATE_POLLING, DISCONNECT_SOCKET)),
        takeWhile(() => hasAnyOpenPollingAsset(state$), true),
        filter(() => {
          const last = getHoldingsApTimestampSelector(state$.value); // 0 if never
          // ? need to add 50ms for date.now function execution
          const drift = 50;
          const shouldContinue =
            (last === 0 || Date.now() - last >= REST_MODE_DELAY - drift) &&
            hasAnyOpenPollingAsset(state$) &&
            isOnlineSelector(state$.value);
          console.log('should trigger polling api calls', shouldContinue);
          return shouldContinue;
        }),
        mergeMap(() => [dispatchHoldingApi(), setHoldingApisTimeStamp()]),
        catchError(err => {
          console.log('DEBUG', err);
          return EMPTY;
        })
      );
    })
  );

export const holdingsActionEpic: Epic<any, any, IState> = action$ =>
  action$.pipe(
    ofType(DISPATCH_HOLDINGS_API),
    mergeMap(action => {
      const { isUserTriggered } = action?.payload ?? {};
      if (isUserTriggered) {
        const refreshId = isUserTriggered ? timeNow().valueOf().toString() : undefined;

        const meta = {
          isUserTriggered,
          refreshId,
          trackAbortFailure: true,
        };
        return [
          dispatchHoldingsApiStart(isUserTriggered, refreshId),
          getPositionsFromApi(meta),
          getOpenOrdersFromApi(undefined, meta),
          getOpenStopOrdersFromApi(undefined, meta),
          getBalances(meta),
        ];
      }

      return [
        getPositionsFromApi(),
        getOpenOrdersFromApi(),
        getOpenStopOrdersFromApi(),
        getBalances(),
      ];
    }),
    catchError(err => {
      console.log('DEBUG', err);
      return EMPTY;
    })
  );

export const pollingTriggers: Epic<any, any, IState> = (action$, state$) =>
  action$.pipe(
    ofType(
      TAB_CHANGE,
      SET_AUTH_SOCKET_CHANNEL_STATUS,
      LOCATION_CHANGE,
      DESKTOP_WALLET_OPEN
    ),
    filter(() => isOnlineSelector(state$.value)),
    mergeMap(({ type, payload }) => {
      const shouldPoll = shouldStartPolling({ type, payload, state$ });
      return [shouldPoll ? manualRestMode() : deactivateRestMode()];
    })
  );

export const reTriggerPolling: Epic<any, any, IState> = (action$, state$) => {
  return action$.pipe(
    ofType(
      TRADE.EDIT_BRACKET_ORDER.SUCCESS,
      TRADE.ORDER.SUCCESS,
      TRADE.ORDER_CANCEL.SUCCESS,
      TRADE.CANCEL_ALL_ORDER.SUCCESS,
      TRADE.CLOSE_ALL_POSITION.SUCCESS,
      TRADE.CANCEL_BRACKET_ORDER_POSITION.SUCCESS,
      TRADE.EDIT_BRACKET_ORDER_POSITION.SUCCESS,
      TRADE.BRACKET_ORDER.SUCCESS,
      TRADE.CLOSE_POSITION.SUCCESS,
      TRADE.PLACE_BRACKET_ORDER_POSITION.SUCCESS,
      TRADE.UPDATE_BRACKET_ORDER_FROM_OPEN_ORDER.SUCCESS,
      TRADE.CLOSE_POSITION_BASKET.SUCCESS,
      TRADE.CANCEL_BRACKET_ORDER.SUCCESS,
      BASKET_ORDERS.PLACE_ORDER.SUCCESS,
      TRADE.POSITION_AUTO_TOPUP.SUCCESS
    ),
    filter(
      () =>
        isAuthenticated(state$.value.user) &&
        selectIsAuthChannelFailed(state$.value) &&
        isOnlineSelector(state$.value)
    ),
    mergeMap(() => {
      if (isRNWebview()) {
        postMessageToMobileApp(POST_MESSAGE_TO_MOBILE_EVENTS.UPDATE_ORDER_POSITION);
      }
      return [
        dispatchHoldingApi(),
        deactivateRestMode(),
        manualRestMode({ retrigger: true }),
      ];
    }),
    catchError(err => {
      console.log('DEBUG', err);
      return EMPTY;
    })
  );
};

export const retryAuthChannel: Epic<any, any, IState> = (action$, state$) =>
  action$.pipe(
    ofType(AUTH_CHANNEL_RETRY),
    filter(
      () =>
        isAuthenticated(state$.value.user) &&
        selectIsAuthChannelFailed(state$.value) &&
        isOnlineSelector(state$.value)
    ),
    switchMap(() => {
      return timer(RETRY_AUTHCHANNEL_DELAY, RETRY_AUTHCHANNEL_DELAY).pipe(
        takeUntil(
          action$.pipe(
            ofType(SET_AUTH_SOCKET_CHANNEL_STATUS),
            filter(a => a?.payload === AUTH_CHANNEL_STATUS.CONNECTED)
          )
        ),
        filter(() => isOnlineSelector(state$.value)),
        map(() => {
          console.log('sending auth retry');
          return { type: AUTHENTICATE_SOCKET, payload: { retry: true } };
        }),
        catchError(err => {
          console.log('DEBUG', err);
          return EMPTY;
        })
      );
    })
  );

// export const rmTicker = (action$, state$) =>
//   action$.pipe(
//     ofType(ACTIVATE_REST_MODE),
//     switchMap(() => {
//       return timer(0, REST_MODE_DELAY).pipe(
//         map(() => {
//           return getAllTickers();
//         }),
//         takeUntil(action$.ofType(DEACTIVATE_REST_MODE)),
//         catchError(err => {
//           console.log('DEBUG', err);
//           return EMPTY;
//         })
//       );
//     })
//   );

export default combineEpics(
  rmHoldingsApi,
  holdingsActionEpic,
  pollingTriggers,
  reTriggerPolling,
  retryAuthChannel
);
